import React, { useState } from 'react';
import { useEffect} from 'react';
import { useContext } from 'react';
import { useRef } from 'react';
import { AuthContext } from '../AuthContext';
import HolidayCalculator from './holidayhourscal';
import SicknessCalculator from './sicknesscalculator';
import PayDates from './paydates';
import UKPolicies from './UKPolicies';

export default function PolicyForm() {
    const [open, setOpen] = useState(false);
    const [showUKPolicies, setShowUKPolicies] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingPolicyId, setEditingPolicyId] = useState(null);
    const [formData, setFormData] = useState({
        SickEpisodes: '',
        SickDays: '',
        LateOccasions: '',
        HolidayEntitlement: '',
        ProbationPeriod: '',
        NoticePeriod: '',
        AppraisalFrequency: '1',
        DaysOpen: [],
        BankHoliday : '',
        HolidayType : '',
        Formula : '',
        ExtraDay : '',
        HolidayStart : '',
        HolidayEnd: '',
        MaxDays : '',
        MaternityPolicy: '',
    });

    const [policy, setPolicy] = useState([]);
    const [openTab, setOpenTab] = useState('days');
    const [scrollIndicators, setScrollIndicators] = useState({ left: false, right: false });
    const tableScrollRef = useRef(null);
    const [payDatesKey, setPayDatesKey] = useState(0);

    const user = useContext(AuthContext);
    const token = localStorage.getItem('token');

    // Handle checkbox changes for DaysOpen
    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        setFormData((prevData) => {
            const newDaysOpen = checked
                ? [...prevData.DaysOpen, value]
                : prevData.DaysOpen.filter((day) => day !== value);
            return {
                ...prevData,
                DaysOpen: newDaysOpen,
            };
        });
    };

    const hasPolicy = policy.length > 0;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const fetchPolicies = async () => {
        const response = await fetch("https://api.insightos.co.uk/api/policies", {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (response.ok) {
            const data = await response.json();
            setPolicy(data.data);
        } else {
            const error = await response.json();
            console.error('Error:', error);
        }
    };

    useEffect(() => {
        fetchPolicies();
    }, []);

    useEffect(() => {
        const node = tableScrollRef.current;
        if (!node) return;

        const updateIndicators = () => {
            const { scrollLeft, scrollWidth, clientWidth } = node;
            setScrollIndicators({
                left: scrollLeft > 0,
                right: scrollLeft + clientWidth < scrollWidth,
            });
        };

        updateIndicators();
        node.addEventListener('scroll', updateIndicators);
        window.addEventListener('resize', updateIndicators);

        return () => {
            node.removeEventListener('scroll', updateIndicators);
            window.removeEventListener('resize', updateIndicators);
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const url = editMode
            ? `https://api.insightos.co.uk/api/policies/${editingPolicyId}`
            : "https://api.insightos.co.uk/api/policies";
        const method = editMode ? 'PUT' : 'POST';

        const response = await fetch(url, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(formData),
        });

        if (response.ok) {
            const result = await response.json();
            console.log(editMode ? 'Policy updated successfully:' : 'Policy added successfully:', result);
            setFormData({
                SickEpisodes: '',
                SickDays: '',
                LateOccasions: '',
                HolidayEntitlement: '',
                ProbationPeriod: '',
                NoticePeriod: '',
                AppraisalFrequency: '1',
                DaysOpen: [],
                BankHoliday : '',
                HolidayType : '',
                Formula : '',
                ExtraDay : '',
                HolidayStart : '',
                HolidayEnd: '',
                MaxDays : '',
                MaternityPolicy: '',
            });
            setEditMode(false);
            setEditingPolicyId(null);
            setOpen(false);
            fetchPolicies();
            setPayDatesKey(prev => prev + 1);
        } else {
            const error = await response.json();
            console.error('Error:', error);
        }
    };

    const handleEdit = (policyItem) => {
        setEditMode(true);
        setEditingPolicyId(policyItem.id);
        let daysOpenArray = [];
        if (typeof policyItem.DaysOpen === 'string') {
            daysOpenArray = policyItem.DaysOpen.split(', ').map(day => day.trim());
        } else if (Array.isArray(policyItem.DaysOpen)) {
            daysOpenArray = policyItem.DaysOpen;
        }

        setFormData({
            SickEpisodes: policyItem.SickEpisodes || '',
            SickDays: policyItem.SickDays || '',
            LateOccasions: policyItem.LateOccasions || '',
            HolidayEntitlement: policyItem.HolidayEntitlement || '',
            ProbationPeriod: policyItem.ProbationPeriod || '',
            NoticePeriod: policyItem.NoticePeriod || '',
            AppraisalFrequency: policyItem.AppraisalFrequency || '1',
            DaysOpen: daysOpenArray,
            BankHoliday: policyItem.BankHoliday || '',
            HolidayType: policyItem.HolidayType || '',
            Formula: policyItem.Formula || '',
            ExtraDay: policyItem.ExtraDay || '',
            HolidayStart: policyItem.HolidayStart || '',
            HolidayEnd: policyItem.HolidayEnd || '',
            MaxDays: policyItem.MaxDays || '',
            MaternityPolicy: policyItem.MaternityPolicy || '',
        });
        setOpen(true);
    };

    const deletePolicy = async (policyId) => {
        const response = await fetch("https://api.insightos.co.uk/api/deletepolicy", {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ id: policyId }),
        });

        if (response.ok) {
            const result = await response.json();
            console.log('Policy deleted successfully:', result);
            fetchPolicies();
            setPayDatesKey(prev => prev + 1);
        } else {
            const error = await response.json();
            console.error('Error:', error);
        }
    };

    const handleHolidayChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        if (value === 'Hours') {
            setOpenTab('Hours');
        } else {
            setOpenTab('Days');
        }
    };

    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatYesNo = (value) => {
        if (value === null || value === undefined || value === '') return 'Not set';
        return value === 1 || value === '1' || value === true ? 'Yes' : 'No';
    };

    const formatHolidayWindow = (start, end) => {
        if (!start && !end) return 'Not set';
        if (start && end) return `${formatDate(start)} - ${formatDate(end)}`;
        if (start) {
            const fallbackEnd = new Date(start);
            if (!Number.isNaN(fallbackEnd.getTime())) {
                fallbackEnd.setDate(fallbackEnd.getDate() + 365);
                return `${formatDate(start)} - ${formatDate(fallbackEnd)}`;
            }
            return formatDate(start);
        }
        return formatDate(end);
    };

    const displayDaysOpen = (days) => {
        if (!days) return 'Not set';
        if (Array.isArray(days)) return days.join(', ');
        return days;
    };

    const summarizeText = (text, limit = 140) => {
        if (!text) return 'No policy provided yet';
        return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
    };

    const parsePayDatePayload = (value) => {
        if (!value) return null;
        if (typeof value === 'object') return value;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                    return JSON.parse(trimmed);
                } catch (error) {
                    console.warn('Failed to parse pay date JSON', error);
                }
            }
        }
        return null;
    };

    const formatPayDateShort = (dateString) => {
        if (!dateString) return '';
        const parsed = new Date(dateString);
        if (Number.isNaN(parsed.getTime())) return '';
        return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    };

    const getPaySchedulePresentation = (policy) => {
        const payload = parsePayDatePayload(policy?.PayDate);
        const scheduleLabel = payload?.schedule || policy?.PaySchedule || 'Schedule not set';

        if (payload && Array.isArray(payload.dates) && payload.dates.length > 0) {
            const preview = payload.dates
                .slice(0, 3)
                .map(formatPayDateShort)
                .filter(Boolean)
                .join(', ');
            const remainder = payload.dates.length - Math.min(payload.dates.length, 3);
            const detail = preview
                ? `Next: ${preview}${remainder > 0 ? ` (+${remainder} more)` : ''}`
                : 'Configure pay dates to preview upcoming runs';
            return { scheduleLabel, detail };
        }

        if (policy?.PaySchedule === 'Weekly' && policy?.PayDay) {
            return { scheduleLabel, detail: `Paid weekly every ${policy.PayDay}` };
        }

        if (policy?.PaySchedule === 'Bi-Weekly' && (policy?.PayDate || policy?.PayDateBi)) {
            return { scheduleLabel, detail: `Paid on ${policy.PayDate || '—'} & ${policy.PayDateBi || '—'}` };
        }

        if (policy?.PaySchedule === '4 Weekly' && policy?.PayDate) {
            return { scheduleLabel, detail: `Cycle from ${formatDate(policy.PayDate)}` };
        }

        if (policy?.PaySchedule === 'Monthly' && policy?.PayDate) {
            return { scheduleLabel, detail: `Paid on day ${policy.PayDate}` };
        }

        return { scheduleLabel, detail: 'Configure pay dates to preview upcoming runs' };
    };

    const cardClassName = 'rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 p-4 sm:p-6 shadow-[0_12px_30px_rgba(99,102,241,0.2)] backdrop-blur-xl';

    return (
        <div className="space-y-8">
            <div className="px-4 sm:px-6 lg:px-8">
                <div className={cardClassName}>
                    <div className="sm:flex sm:items-center">
                        <div className="sm:flex-auto">
                            <h1 className="text-base font-semibold text-indigo-950">Company Policies</h1>
                            <p className="mt-2 text-sm text-indigo-900/80">
                                Manage your company policies and staff entitlements.
                            </p>
                        </div>
                        { user.access === 'admin' &&
                            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                                <div className="flex gap-2">
                                    {!hasPolicy && <button onClick={() => {
                                        setEditMode(false);
                                        setEditingPolicyId(null);
                                        setFormData({
                                            SickEpisodes: '',
                                            SickDays: '',
                                            LateOccasions: '',
                                            HolidayEntitlement: '',
                                            ProbationPeriod: '',
                                            NoticePeriod: '',
                                            AppraisalFrequency: '1',
                                            DaysOpen: [],
                                            BankHoliday : '',
                                            HolidayType : '',
                                            Formula : '',
                                            ExtraDay : '',
                                            HolidayStart : '',
                                            HolidayEnd: '',
                                            MaxDays : '',
                                            MaternityPolicy: '',
                                        });
                                        setOpen(true);
                                    }}
                                        type="button"
                                        data-component="policy-listing"
                                        data-feature="open_policy_form"
                                        className="block rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/15 px-4 py-2 text-center text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    >
                                        Add Policy
                                    </button>}
                                    <button
                                        onClick={() => setShowUKPolicies(true)}
                                        type="button"
                                        data-component="policy-listing"
                                        data-feature="open_uk_hr_policies"
                                        className="block rounded-xl border border-indigo-200 bg-white/80 px-4 py-2 text-center text-sm font-semibold text-indigo-900 shadow-sm hover:bg-white"
                                    >
                                        Open UK HR Policies
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <div className="mt-8">
                    <div className={cardClassName}>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                            <span>Policy overview</span>
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400">
                                Drag sideways to explore
                                <svg
                                    className="h-3.5 w-3.5 text-gray-400"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    aria-hidden="true"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 5l-5 7 5 7M16 5l5 7-5 7M9 12h6" />
                                </svg>
                            </span>
                        </div>
                        <div className="relative">
                            <div
                                className={`pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent transition-opacity duration-300 ${scrollIndicators.left ? 'opacity-100' : 'opacity-0'}`}
                                aria-hidden="true"
                            />
                            <div
                                className={`pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent transition-opacity duration-300 ${scrollIndicators.right ? 'opacity-100' : 'opacity-0'}`}
                                aria-hidden="true"
                            />
                            <div
                                ref={tableScrollRef}
                                className="overflow-x-auto px-4 pb-4 pt-2 sm:px-6 lg:px-8"
                            >
                                <table className="min-w-[1280px] border-separate border-spacing-y-3">
                                    <thead>
                                        <tr>
                                            <th scope="col" className="whitespace-nowrap px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                Absence & Attendance
                                            </th>
                                            <th scope="col" className="whitespace-nowrap px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                Employment Terms
                                            </th>
                                            <th scope="col" className="whitespace-nowrap px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                Holiday Framework
                                            </th>
                                            <th scope="col" className="whitespace-nowrap px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                Holiday Extras
                                            </th>
                                            <th scope="col" className="whitespace-nowrap px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                Payment Schedule
                                            </th>
                                            <th scope="col" className="whitespace-nowrap px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                Operating Days
                                            </th>
                                            <th scope="col" className="whitespace-nowrap px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                Maternity Policy
                                            </th>
                                            <th scope="col" className="relative px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">
                                                <span className="sr-only">Actions</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {policy.map((policyItem,index) => {
                                            const schedulePresentation = getPaySchedulePresentation(policyItem);
                                            return (
                                                <tr key={index} className="bg-white/95 shadow-sm ring-1 ring-indigo-100 transition-all hover:ring-indigo-200">
                                                    <td className="whitespace-nowrap px-4 py-4 text-sm rounded-l-xl">
                                                        <div className="font-semibold text-gray-900">
                                                            {policyItem.SickEpisodes ? `${policyItem.SickEpisodes} Sick Episodes` : 'Sick policy not set'}
                                                        </div>
                                                        <div className="text-gray-600">
                                                            {policyItem.SickDays ? `${policyItem.SickDays} Sick Days` : '—'}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {policyItem.LateOccasions ? `${policyItem.LateOccasions} Late Occasions / Year` : 'Late policy not set'}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                                        <div className="font-semibold text-gray-900">
                                                            {policyItem.ProbationPeriod ? `${policyItem.ProbationPeriod} Month Probation` : 'Probation not set'}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {policyItem.AppraisalFrequency ? `Appraisal every ${policyItem.AppraisalFrequency} month(s)` : 'Appraisal cadence not set'}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {policyItem.NoticePeriod ? `Notice period: ${policyItem.NoticePeriod} week(s)` : 'Notice period not set'}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                                        <div className="font-semibold text-gray-900">
                                                            {policyItem.HolidayType || 'Holiday type not set'}
                                                        </div>
                                                        <div>
                                                            {policyItem.HolidayEntitlement ? `${policyItem.HolidayEntitlement} days FT entitlement` : 'Holiday entitlement pending'}
                                                        </div>
                                                        <div className="text-xs text-gray-500">Formula: {policyItem.Formula || 'Not provided'}</div>
                                                        <div className="text-xs text-gray-500">Bank Holidays: {formatYesNo(policyItem.BankHoliday)}</div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                                        <div className="font-semibold text-gray-900">Extra Day: {formatYesNo(policyItem.ExtraDay)}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {policyItem.MaxDays ? `Max ${policyItem.MaxDays} day(s)` : 'No max set'}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            Window: {formatHolidayWindow(policyItem.HolidayStart, policyItem.HolidayEnd)}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                                        <div className="font-semibold text-gray-900">
                                                            {schedulePresentation.scheduleLabel}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {schedulePresentation.detail}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">{displayDaysOpen(policyItem.DaysOpen)}</td>
                                                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600 max-w-xs">
                                                        <p className="text-xs text-gray-500 leading-5">
                                                            {summarizeText(policyItem.MaternityPolicy)}
                                                        </p>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-4 text-right text-sm font-medium rounded-r-xl">
                                                        <div className="flex gap-2 justify-end">
                                                            { user.access === 'admin' && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleEdit(policyItem)}
                                                                        type="button"
                                                                        data-component="policy-listing"
                                                                        data-feature="edit_policy"
                                                                        data-policy-index={index}
                                                                        className="rounded-xl bg-blue-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        onClick={() => deletePolicy(policyItem.id)}
                                                                        type="button"
                                                                        data-component="policy-listing"
                                                                        data-feature="delete_policy_delete"
                                                                        data-policy-index={index}
                                                                        className="rounded-xl bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Pay Dates Generator ───────────────────────────────────── */}
            <PayDates
                key={payDatesKey}
                canManage={user.access === 'admin'}
                onSaveComplete={fetchPolicies}
            />

            <UKPolicies open={showUKPolicies} onClose={() => setShowUKPolicies(false)} />

            {open && (
                <form onSubmit={handleSubmit} className="rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/90 to-purple-100/80 p-6 shadow-[0_12px_30px_rgba(99,102,241,0.2)] backdrop-blur-xl">
                    <div className="space-y-12">
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-lg font-semibold leading-7 text-gray-900">Company Policies</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                Define your company's policies for staff entitlements and working conditions.
                            </p>

                            {/* Sickness Policy Section */}
                            <div className="mt-10">
                                <h3 className="text-base font-semibold leading-7 text-gray-900 border-b border-gray-200 pb-2">
                                    Absence & Attendance Policy
                                </h3>
                                <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-2">
                                        <label htmlFor="SickEpisodes" className="block text-sm font-medium leading-6 text-gray-900">
                                            Sick Episodes Per Year
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id="SickEpisodes"
                                                name="SickEpisodes"
                                                type="number"
                                                value={formData.SickEpisodes}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="SickDays" className="block text-sm font-medium leading-6 text-gray-900">
                                            Sick Days Per Year
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id="SickDays"
                                                name="SickDays"
                                                type="number"
                                                value={formData.SickDays}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="LateOccasions" className="block text-sm font-medium leading-6 text-gray-900">
                                            Late Occasions Per Year
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id="LateOccasions"
                                                name="LateOccasions"
                                                type="number"
                                                value={formData.LateOccasions}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Holiday Policy Section */}
                            <div className="mt-10">
                                <h3 className="text-base font-semibold leading-7 text-gray-900 border-b border-gray-200 pb-2">
                                    Holiday Entitlement Policy
                                </h3>
                                <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-4">
                                        <label htmlFor="HolidayType" className="block text-sm font-medium leading-6 text-gray-900">
                                            How Do You Work Out Holiday Entitlement Per Year?
                                        </label>
                                        <div className="mt-2">
                                            <select
                                                id="HolidayType"
                                                name="HolidayType"
                                                value={formData.HolidayType}
                                                onChange={handleHolidayChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            >
                                                <option value="">Select method</option>
                                                <option value="Hours">Hours</option>
                                            </select>
                                        </div>
                                    </div>
                                    {openTab === 'Days' && (
                                        <div className="sm:col-span-4">
                                            <label htmlFor="HolidayEntitlement" className="block text-sm font-medium leading-6 text-gray-900">
                                                Holiday Entitlement Full Time (Days)
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    id="HolidayEntitlement"
                                                    name="HolidayEntitlement"
                                                    type="number"
                                                    value={formData.HolidayEntitlement}
                                                    onChange={handleChange}
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {openTab === 'Hours' && (
                                        <div className="sm:col-span-4">
                                            <HolidayCalculator />
                                            <div className="sm:col-span-4">
                                                <label htmlFor="Formula" className="block animate-pulse text-sm mt-2 font-medium leading-6 text-gray-900">
                                                    Formula for calculating holiday entitlement (5.6 Weeks - UK Standard)
                                                </label>
                                                <div className="mt-2">
                                                    <input
                                                        id="Formula"
                                                        name="Formula"
                                                        type="number"
                                                        value={formData.Formula}
                                                        onChange={handleChange}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Employment Terms Section */}
                            <div className="mt-10">
                                <h3 className="text-base font-semibold leading-7 text-gray-900 border-b border-gray-200 pb-2">
                                    Employment Terms
                                </h3>
                                <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-3">
                                        <label htmlFor="ProbationPeriod" className="block text-sm font-medium leading-6 text-gray-900">
                                            Probation Period (Months)
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id="ProbationPeriod"
                                                name="ProbationPeriod"
                                                type="number"
                                                value={formData.ProbationPeriod}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label htmlFor="NoticePeriod" className="block text-sm font-medium leading-6 text-gray-900">
                                            Notice Period (Weeks)
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id="NoticePeriod"
                                                name="NoticePeriod"
                                                type="number"
                                                min="0"
                                                value={formData.NoticePeriod}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label htmlFor="AppraisalFrequency" className="block text-sm font-medium leading-6 text-gray-900">
                                            Appraisal Frequency (Months)
                                        </label>
                                        <div className="mt-2">
                                            <select
                                                id="AppraisalFrequency"
                                                name="AppraisalFrequency"
                                                value={formData.AppraisalFrequency}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            >
                                                <option value="1">1</option>
                                                <option value="3">3</option>
                                                <option value="6">6</option>
                                                <option value="12">12</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Schedule Section */}
                            <div className="mt-10">
                                <h3 className="text-base font-semibold leading-7 text-gray-900 border-b border-gray-200 pb-2">
                                    Payment Schedule
                                </h3>
                                <p className="mt-4 text-sm text-gray-600">
                                    Payment frequencies and future pay dates are now configured through the Pay Dates generator on this page. Use that tool to
                                    build the next 12 months of pay runs and store them as JSON.
                                </p>
                            </div>

                            {/* Additional Holiday Settings */}
                            <div className="mt-10">
                                <h3 className="text-base font-semibold leading-7 text-gray-900 border-b border-gray-200 pb-2">
                                    Additional Holiday Settings
                                </h3>
                                <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-2">
                                        <label htmlFor="BankHoliday" className="block text-sm font-medium leading-6 text-gray-900">
                                            Open Bank Holidays
                                        </label>
                                        <div className="mt-2">
                                            <select
                                                id="BankHoliday"
                                                name="BankHoliday"
                                                value={formData.BankHoliday}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            >
                                                <option value="1">Yes</option>
                                                <option value="0">No</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="ExtraDay" className="block text-sm font-medium leading-6 text-gray-900">
                                            Extra Day Holiday For Each Year Worked
                                        </label>
                                        <div className="mt-2">
                                            <select
                                                id="ExtraDay"
                                                name="ExtraDay"
                                                value={formData.ExtraDay}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            >
                                                <option value="0">No</option>
                                                <option value="1">Yes</option>
                                            </select>
                                        </div>
                                    </div>
                                    {formData.ExtraDay === '1' &&
                                        <div className="sm:col-span-2">
                                            <label htmlFor="MaxDays" className="block text-sm font-medium leading-6 text-gray-900">
                                                Maximum Extra Days
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    id="MaxDays"
                                                    name="MaxDays"
                                                    type="number"
                                                    value={formData.MaxDays}
                                                    onChange={handleChange}
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                />
                                            </div>
                                        </div>
                                    }
                                    <div className="sm:col-span-3">
                                        <label htmlFor='HolidayStart' className="block text-sm font-medium leading-6 text-gray-900">
                                            Holiday Window Start
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id="HolidayStart"
                                                name="HolidayStart"
                                                type="date"
                                                value={formData.HolidayStart}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label htmlFor='HolidayEnd' className="block text-sm font-medium leading-6 text-gray-900">
                                            Holiday Window End
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id="HolidayEnd"
                                                name="HolidayEnd"
                                                type="date"
                                                value={formData.HolidayEnd}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Maternity Policy */}
                            <div className="mt-10">
                                <h3 className="text-base font-semibold leading-7 text-gray-900 border-b border-gray-200 pb-2">
                                    Maternity Pay Policy
                                </h3>
                                <div className="mt-6">
                                    <label htmlFor="MaternityPolicy" className="block text-sm font-medium leading-6 text-gray-900">
                                        Policy Details
                                    </label>
                                    <div className="mt-2">
                                        <textarea
                                            id="MaternityPolicy"
                                            name="MaternityPolicy"
                                            rows={6}
                                            value={formData.MaternityPolicy}
                                            onChange={handleChange}
                                            placeholder="Enter your company's maternity pay policy details, including eligibility criteria, duration, payment terms, and any additional benefits..."
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                    <p className="mt-2 text-sm leading-6 text-gray-600">
                                        Describe your maternity pay policy including eligibility requirements, notice period, duration of leave, statutory and enhanced pay provisions, and return-to-work arrangements.
                                    </p>
                                </div>
                            </div>

                            {/* Days Open Section */}
                            <div className="mt-10">
                                <h3 className="text-base font-semibold leading-7 text-gray-900 border-b border-gray-200 pb-2">
                                    Operating Days
                                </h3>
                                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
                                    {daysOfWeek.map((day) => (
                                        <div key={day} className="relative flex items-start">
                                            <div className="flex h-6 items-center">
                                                <input
                                                    id={day}
                                                    name="DaysOpen"
                                                    type="checkbox"
                                                    value={day}
                                                    checked={formData.DaysOpen?.includes(day) || false}
                                                    onChange={handleCheckboxChange}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                />
                                            </div>
                                            <div className="ml-3 text-sm leading-6">
                                                <label htmlFor={day} className="font-medium text-gray-900">
                                                    {day}
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-end gap-x-3">
                            <button
                                onClick={() => setOpen(false)}
                                type="button"
                                data-component="policy-form"
                                data-feature="cancel_policy_form"
                                className="rounded-xl border border-indigo-200 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-900 shadow-sm hover:bg-white"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-component="policy-form"
                                data-time-saved="30"
                                data-feature={editMode ? "update_policy_put" : "save_policy_post"}
                                className="rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/15 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 focus:outline focus:outline-2 focus:outline-indigo-600"
                            >
                                {editMode ? 'Update Policy' : 'Save Policy'}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}